import React, { useEffect, useState } from 'react';
import { Card, Table, Button, Badge, Modal, Form, Alert } from 'react-bootstrap';
import api from '../services/api';
import { DocumentType, DocumentTypesImportResponse } from '../types';
import { useAuth } from '../context/AuthContext';
import { useArea } from '../context/AreaContext';
import { useModal } from '../context/ModalContext';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../config';
import ActionIconButton from '../components/ActionIconButton';
import { getAuthToken } from '../utils/authToken';

const DocumentTypes: React.FC = () => {
  const { user: currentUser } = useAuth();
  const { currentArea } = useArea();
  const { showAlert, showConfirm } = useModal();
  const navigate = useNavigate();
  const [documentTypes, setDocumentTypes] = useState<DocumentType[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingType, setEditingType] = useState<DocumentType | null>(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importResult, setImportResult] = useState<DocumentTypesImportResponse | null>(null);
  const [importing, setImporting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    is_active: true
  });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (currentUser?.role === 'admin' || currentUser?.role === 'analista') {
      fetchDocumentTypes();
    }
  }, [currentUser, currentArea]);

  const fetchDocumentTypes = async () => {
    try {
      setLoading(true);
      const params: any = {};
      if (currentArea) {
        params.area_type = currentArea;
      }
      const response = await api.get('/api/documents/types', { params });
      setDocumentTypes(response.data);
    } catch (error) {
      console.error('Erro ao carregar tipos de documento:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingType(null);
    setFormData({
      name: '',
      description: '',
      is_active: true
    });
    setError(null);
    setShowModal(true);
  };

  const handleEdit = (type: DocumentType) => {
    setEditingType(type);
    setFormData({
      name: type.name,
      description: type.description || '',
      is_active: type.is_active
    });
    setError(null);
    setShowModal(true);
  };

  const handleSave = async () => {
    try {
      setError(null);
      if (editingType) {
        await api.put(`/api/documents/types/${editingType.id}`, formData);
      } else {
        await api.post('/api/documents/types', {
          ...formData,
          area_type: currentArea || 'purchases'
        });
      }
      setShowModal(false);
      fetchDocumentTypes();
    } catch (error: any) {
      setError(error.response?.data?.detail || error.message || 'Erro ao salvar tipo de documento');
    }
  };

  const handleDelete = async (type: DocumentType) => {
    const ok = await showConfirm({
      title: 'Excluir tipo de documento',
      message: `Tem certeza que deseja excluir o tipo de documento "${type.name}"?`,
      variant: 'danger',
      confirmLabel: 'Excluir',
      cancelLabel: 'Cancelar',
    });
    if (!ok) return;

    try {
      await api.delete(`/api/documents/types/${type.id}`);
      fetchDocumentTypes();
    } catch (error: any) {
      showAlert({
        title: 'Erro',
        message: error.response?.data?.detail || error.message || 'Erro ao excluir tipo de documento',
        variant: 'danger',
      });
    }
  };

  const handleManageFields = (type: DocumentType) => {
    navigate(`/documents/types/${type.id}/fields`);
  };

  const handleExportType = async (type: DocumentType) => {
    if (!currentArea) {
      showAlert({
        title: 'Atenção',
        message: 'Por favor, selecione uma área antes de exportar.',
        variant: 'warning',
      });
      return;
    }

    try {
      const token = getAuthToken();
      const response = await fetch(
        `${API_URL}/api/documents/types/export?area_type=${currentArea}&document_type_id=${type.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!response.ok) {
        throw new Error('Erro ao exportar tipo de documento');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;

      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `document_type_${type.name.replace(/\s+/g, '_').toLowerCase()}.json`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error: any) {
      showAlert({
        title: 'Erro',
        message: error.message || 'Erro ao exportar tipo de documento',
        variant: 'danger',
      });
    }
  };

  const handleImportFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!currentArea) {
      showAlert({
        title: 'Atenção',
        message: 'Por favor, selecione uma área antes de importar.',
        variant: 'warning',
      });
      return;
    }

    const ok = await showConfirm({
      title: 'Importar tipos de documento',
      message: 'Esta operação irá sobrescrever tipos de documento existentes com o mesmo nome e área. Deseja continuar?',
      variant: 'warning',
      confirmLabel: 'Continuar',
      cancelLabel: 'Cancelar',
    });
    if (!ok) {
      event.target.value = '';
      return;
    }

    setImporting(true);
    setImportResult(null);

    try {
      const fileContent = await file.text();
      const payload = JSON.parse(fileContent);

      if (payload.area_type !== currentArea) {
        showAlert({
          title: 'Área incompatível',
          message: `O arquivo exportado é da área "${payload.area_type}", mas você está na área "${currentArea}".`,
          variant: 'warning',
        });
        event.target.value = '';
        return;
      }

      const response = await api.post('/api/documents/types/import', payload);
      setImportResult(response.data);
      setShowImportModal(true);
      await fetchDocumentTypes();
    } catch (error: any) {
      showAlert({
        title: 'Erro ao importar',
        message: error.response?.data?.detail || error.message || 'Erro ao importar tipos de documento',
        variant: 'danger',
      });
    } finally {
      setImporting(false);
      event.target.value = '';
    }
  };

  if (currentUser?.role !== 'admin' && currentUser?.role !== 'analista') {
    return (
      <div className="container mt-4">
        <Alert variant="warning">Você não tem permissão para acessar esta página.</Alert>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Tipos de Documentos</h2>
        <div className="d-flex gap-2">
          <input
            type="file"
            accept=".json"
            onChange={handleImportFile}
            style={{ display: 'none' }}
            id="document-types-import-file-input"
            disabled={importing}
          />
          <Button
            variant="outline-secondary"
            onClick={() => document.getElementById('document-types-import-file-input')?.click()}
            disabled={!currentArea || importing}
          >
            {importing ? 'Importando...' : 'Importar Tipos'}
          </Button>
          <Button variant="primary" onClick={handleCreate}>
            Criar Tipo de Documento
          </Button>
        </div>
      </div>

      {loading ? (
        <p>Carregando...</p>
      ) : (
        <Card>
          <Card.Body>
            <Table striped bordered hover>
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Descrição</th>
                  <th>Área</th>
                  <th>Status</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {documentTypes.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center">
                      Nenhum tipo de documento encontrado
                    </td>
                  </tr>
                ) : (
                  documentTypes.map((type) => (
                    <tr key={type.id}>
                      <td>{type.name}</td>
                      <td>{type.description || '-'}</td>
                      <td>
                        <Badge bg={type.area_type === 'purchases' ? 'primary' : 'secondary'}>
                          {type.area_type === 'purchases' ? 'Compras' : 'Tickets'}
                        </Badge>
                      </td>
                      <td>
                        <Badge bg={type.is_active ? 'success' : 'secondary'}>
                          {type.is_active ? 'Ativo' : 'Inativo'}
                        </Badge>
                      </td>
                      <td>
                        <div className="d-flex flex-wrap gap-1">
                          <ActionIconButton
                            icon="✏️"
                            label={`Editar tipo ${type.name}`}
                            variant="outline-primary"
                            onClick={() => handleEdit(type)}
                          />
                          <ActionIconButton
                            icon="🧩"
                            label={`Gerenciar campos de ${type.name}`}
                            variant="outline-info"
                            onClick={() => handleManageFields(type)}
                          />
                          <ActionIconButton
                            icon="⬇️"
                            label={`Exportar tipo ${type.name}`}
                            variant="outline-secondary"
                            onClick={() => handleExportType(type)}
                          />
                          <ActionIconButton
                            icon="🗑️"
                            label={`Excluir tipo ${type.name}`}
                            variant="outline-danger"
                            onClick={() => handleDelete(type)}
                          />
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}

      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            {editingType ? 'Editar Tipo de Documento' : 'Criar Tipo de Documento'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Nome *</Form.Label>
              <Form.Control
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Descrição</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Ativo"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleSave}>
            Salvar
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showImportModal} onHide={() => setShowImportModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Resultado da Importação</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {importResult && (
            <>
              <Alert variant={importResult.success ? 'success' : 'warning'}>
                <strong>{importResult.success ? 'Importação concluída com sucesso!' : 'Importação concluída com erros'}</strong>
                <br />
                Total: {importResult.total_types} | Criados: {importResult.types_created} | Atualizados: {importResult.types_updated}
              </Alert>
              <Table striped bordered size="sm">
                <thead>
                  <tr>
                    <th>Tipo</th>
                    <th>Ação</th>
                    <th>Criados</th>
                    <th>Atualizados</th>
                    <th>Removidos</th>
                    <th>Erros</th>
                  </tr>
                </thead>
                <tbody>
                  {importResult.results.map((result, idx) => (
                    <tr key={idx}>
                      <td>{result.name}</td>
                      <td>{result.action === 'created' ? 'Criado' : 'Atualizado'}</td>
                      <td>{result.fields_created}</td>
                      <td>{result.fields_updated}</td>
                      <td>{result.fields_deleted}</td>
                      <td>{result.errors?.length ? result.errors.join('; ') : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setShowImportModal(false)}>
            Fechar
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default DocumentTypes;
